/**
 * Site-wide canonical constants — single source of truth for anything that
 * needs to mention the brand, the canonical URL, or default SEO metadata.
 *
 * Consumed by:
 *   - useDocumentMeta hook (per-route page meta)
 *   - scripts/generate-sitemap.mjs (build-time sitemap)
 *   - sitemapRoutes.ts (route manifest)
 *   - index.html (default OG + schema)
 *
 * If the production URL changes (e.g. moving off Railway, adding a custom
 * domain redirect), this is the only file the build needs.
 */

export const SITE_URL = 'https://www.getavium.com';

export const SITE_NAME = 'Avium';

// External Instatus status page (the "System Status" links). Single source of
// truth so it can't drift per-surface — it already did: the Merge→Avium rebrand
// left PublicFooter + the server email pointing at the old `mergesoftware`
// subdomain while Sidebar/LoginPage/AuthLayout moved to `avium`. Every client
// "System Status" link imports THIS. (Server mirror: server/src/lib/email.ts.)
export const STATUS_PAGE_URL = 'https://avium.instatus.com';

// Locked slogan — used as default OG title and as part of structured data
// description. Mirrors CLAUDE.md's branding section; if the slogan ever
// changes, update both places in the same release.
export const SITE_SLOGAN = 'See the work · Plan the capacity · Catch the risk';

// One-line description used as the fallback meta description, OG description,
// and schema.org "description" field. Persona-aware so search-engine snippets
// surface the right audience signal (scrum masters + agile coaches) per the
// 2026-05-26 user direction.
export const SITE_DESCRIPTION =
  'Avium is agile reporting that tells scrum masters, agile coaches, and engineering managers what to discuss at standup — Avium Signals detects sprint risk, over-allocation, and velocity drift; Avium Intelligence turns those signals into a plain-English daily briefing.';

// Default Open Graph image. Static file in /public so it can be served at
// the absolute URL link previews require. Used by every route that doesn't
// override its own OG image. This is the purpose-built 1200x630 branded social
// card (generated by scripts/generate-og-card.py) — the right aspect ratio for
// LinkedIn/Slack/etc., so link previews actually render an image.
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-card.png`;

// Twitter handle (without the @) for Twitter Card attribution. Empty = omit
// the meta tag. Update when the brand handle is registered.
export const TWITTER_HANDLE = '';
